"use client";
import { useState, useEffect, useRef } from "react";
import config from "../../../config/config";

export default function SocialStreamSection() {
  const [facebookUrl, setFacebookUrl] = useState("");
  const [dimensions, setDimensions] = useState({ width: 480, height: 520 });
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);

  // Fetch optimized Facebook URL from API
  const fetchOptimizedFacebookUrl = async (containerWidth, containerHeight) => {
    try {
      const deviceType =
        window.innerWidth < 768
          ? "mobile"
          : window.innerWidth < 1024
          ? "tablet"
          : "desktop";

      const response = await fetch(
        `${config.NEXT_PUBLIC_API_URL}/api/facebook/optimized-params?containerWidth=${containerWidth}&containerHeight=${containerHeight}&deviceType=${deviceType}`
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setFacebookUrl(result.data.url);
          setDimensions({
            width: result.data.parameters.width,
            height: result.data.parameters.height,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching Facebook URL:", error);
      // Fallback to manual URL generation
      generateFallbackUrl(containerWidth, containerHeight);
    } finally {
      setLoading(false);
    }
  };

  // Fallback URL generation if API fails
  const generateFallbackUrl = (containerWidth, containerHeight) => {
    const padding = window.innerWidth >= 640 ? 24 : 16;
    const availableWidth = Math.max(280, containerWidth - padding);
    const availableHeight = Math.max(300, containerHeight - padding);

    const pluginWidth = Math.min(500, Math.max(280, availableWidth));
    const pluginHeight = Math.min(600, Math.max(300, availableHeight - 20));

    const smallHeader = pluginWidth < 400;
    const hideCover = pluginHeight < 400;
    const showFacepile = pluginWidth >= 400;

    const fallbackUrl = `https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2FLJP4India&tabs=timeline&width=${pluginWidth}&height=${pluginHeight}&small_header=${smallHeader}&adapt_container_width=true&hide_cover=${hideCover}&show_facepile=${showFacepile}&appId`;

    setFacebookUrl(fallbackUrl);
    setDimensions({ width: pluginWidth, height: pluginHeight });
  };

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        // Fetch optimized URL from API
        fetchOptimizedFacebookUrl(containerWidth, containerHeight);
      }
    };

    // Initial calculation
    updateDimensions();

    // Update on resize (debounced)
    let resizeTimeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateDimensions, 300);
    };

    window.addEventListener("resize", debouncedResize);

    // Update when container size changes
    const resizeObserver = new ResizeObserver(debouncedResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener("resize", debouncedResize);
      resizeObserver.disconnect();
      clearTimeout(resizeTimeout);
    };
  }, []);

  return (
    <div className="col-span-1 lg:col-span-1">
      <div className="bg-white rounded-t-sm border border-gray-200 overflow-hidden flex flex-col h-[400px] sm:h-[500px] md:h-[600px] lg:h-[650px]">
        {/* Header */}
        <div className="bg-primary text-primary-foreground h-10 sm:h-12 flex items-center justify-between px-3 sm:px-4 flex-shrink-0">
          <h2 className="text-base sm:text-lg font-bold">SOCIAL STREAM</h2>
          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-600 flex items-center justify-center rounded">
            <span className="text-xs font-bold text-white">f</span>
          </div>
        </div>

        <div className="flex-1 bg-gray-50 overflow-hidden" ref={containerRef}>
          <div className="p-2 sm:p-3 h-full">
            {loading ? (
              /* Loading State */
              <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-sm">
                <div className="text-center">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">
                    Loading Social Stream...
                  </p>
                </div>
              </div>
            ) : (
              /* Facebook Iframe - API Generated URL */
              <iframe
                key={facebookUrl} // Force re-render when URL changes
                src={facebookUrl}
                width="100%"
                height="100%"
                style={{
                  border: "none",
                  overflow: "hidden",
                  minHeight: `${dimensions.height}px`,
                  minWidth: `${dimensions.width}px`,
                }}
                scrolling="no"
                frameBorder="0"
                allowtransparency="true"
                allow="encrypted-media"
                title="Facebook Page"
                className="rounded-sm w-full h-full"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
